/**
 * Timezone preference for activity timestamps.
 *
 * Audit timestamps in PPPC activity, the global Logs page, and the
 * timeline embeds all need to render in a single, consistent timezone
 * chosen by the admin (e.g., a global SOC operating in UTC, or a
 * tenant-specific region). We persist the choice in localStorage so it
 * survives reloads and propagates across tabs via the standard
 * `storage` event plus an in-tab `timezone-preference-updated`
 * CustomEvent.
 *
 * The stored value is either:
 *   - `"auto"` — fall back to the browser's resolved IANA zone
 *     (`Intl.DateTimeFormat().resolvedOptions().timeZone`), OR
 *   - a valid IANA zone name (e.g. `"UTC"`, `"America/New_York"`).
 *
 * Invalid stored values are treated as "auto" so a corrupted preference
 * never breaks rendering.
 */

const STORAGE_KEY = "smplify-timezone-preference:v1";
export const AUTO_TIMEZONE = "auto" as const;

export type TimezonePreference = typeof AUTO_TIMEZONE | string;

/** Curated short list of zones admins can pick without typing. */
export const COMMON_TIMEZONES: Array<{ value: string; label: string }> = [
  { value: "UTC", label: "UTC" },
  { value: "America/Los_Angeles", label: "Los Angeles (PT)" },
  { value: "America/Denver", label: "Denver (MT)" },
  { value: "America/Chicago", label: "Chicago (CT)" },
  { value: "America/New_York", label: "New York (ET)" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Berlin", label: "Berlin" },
  { value: "Europe/Paris", label: "Paris" },
  { value: "Asia/Dubai", label: "Dubai" },
  { value: "Asia/Kolkata", label: "Kolkata" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Australia/Sydney", label: "Sydney" },
];

/** True when the runtime supports formatting dates in `tz`. */
export function isValidTimezone(tz: string): boolean {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** The browser's resolved IANA zone (or "UTC" as a last resort). */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function getTimezonePreference(): TimezonePreference {
  if (typeof window === "undefined") return AUTO_TIMEZONE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return AUTO_TIMEZONE;
    if (raw === AUTO_TIMEZONE) return AUTO_TIMEZONE;
    return isValidTimezone(raw) ? raw : AUTO_TIMEZONE;
  } catch {
    return AUTO_TIMEZONE;
  }
}

export function setTimezonePreference(pref: TimezonePreference): void {
  if (typeof window === "undefined") return;
  const safe = pref === AUTO_TIMEZONE || isValidTimezone(pref) ? pref : AUTO_TIMEZONE;
  try {
    window.localStorage.setItem(STORAGE_KEY, safe);
  } catch {
    /* quota exceeded */
  }
  window.dispatchEvent(
    new CustomEvent("timezone-preference-updated", { detail: { value: safe } }),
  );
}

/** Resolve a preference to a concrete IANA zone for `Intl` calls. */
export function resolveTimezone(pref: TimezonePreference = getTimezonePreference()): string {
  return pref === AUTO_TIMEZONE ? getBrowserTimezone() : pref;
}

/**
 * Render an ISO timestamp in the resolved zone using a human-friendly
 * "Sep 12, 2025, 3:42 PM PDT" style. Falls back to the raw string when
 * the input isn't a valid date.
 */
export function formatInTimezone(
  iso: string,
  pref: TimezonePreference = getTimezonePreference(),
): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  const timeZone = resolveTimezone(pref);
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone,
      timeZoneName: "short",
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

/**
 * "x min ago" / "just now" / etc. Relative time is intentionally
 * timezone-independent (an instant is an instant), but kept here so
 * every audit surface uses identical thresholds and pluralization.
 */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  const sec = Math.round((now - t) / 1000);
  if (sec < 0) return "in the future";
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  return formatInTimezone(iso);
}
